/*
  README - BEFORE MAKING ANY EDITS
  There are a lot of complex scenairos taking place in this code. The smallest of
  changes can have disastrous results for the application.  In order to modify this method
  the following conditions must be met:

  1. You have to be resolving a bug or adding new asynchronous features
  2. Your name must be Christopher Aaron Jones

  If you do not meet any of the above conditions do not modify this code under
  any circumstances on the penalty of death.
*/
import React from "react"
import { useEffect, useReducer, useCallback, Dispatch, useRef } from "react"
import firebase, { db as dbFs } from "../../mp-database/firebaseConfig"
import * as yup from "yup"

import _ from "lodash"
import { default as isEqual } from "fast-deep-equal/es6/react"

import {
  Doc,
  Collection,
  onGet as tsOnGet,
  get as tsGet,
  set as tsSet,
  ref as tsRef,
  Ref,
  remove as tsDel
} from "typesaurus"
import actionCreatorFactory, { Action } from "typescript-fsa"
import { reducerWithoutInitialState } from "typescript-fsa-reducers"

import { db, CollectionTypes, KeysOfType, reducers } from "."
import { updateWithMeta } from "./helpers"
import {
  dispatchWrapperForActionsOfCollection,
  CollectionActions,
  CollectionDispatchedActions
} from "./collections"
import { dispatchWrapperForNode, GetNodefn } from "./reducers"
import { typeSchema, collectionSchema } from "./schemas"

type ValidationState = "PENDING" | "DONE" | "PROCESSING"

const validationOptions = {
  strict: true,
  abortEarly: false,
  stripUnknown: true,
  recursive: true
}

// Dao State Actions
const actionCreator = actionCreatorFactory("DAO")
const setDoc = actionCreator<Doc<any>>("doc")
const setData = actionCreator<{ collection: Collection<any>; id: string }>(
  "data"
)
const loadDoc = actionCreator<CallbackFn>("loadDoc")
const resetStateAndLoad = actionCreator<CallbackFn>("resetStateAndLoad")
const setValidation = actionCreator<ValidationState>("validation")
const setDispatch = actionCreator<Dispatch<any>>("dispatch")
const setSetProp = actionCreator<CollectionDispatchedActions<any>>("setProp")
const setRefreshState = actionCreator<CallbackFn>("refreshState")
const setNodeProp = actionCreator<GetNodefn<any>>("nodeProp")
const setSaveDocFn = actionCreator<any>("setSaveDocFn")
const saveDoc = actionCreator<CallbackFn>("saveDoc")
const clearCallback = actionCreator("clearCallback")
const clearDirtyFlag = actionCreator("clearDirtyFlag")

type CallbackFn = () => void

// type OnResult<Model> = (doc: Doc<Model> | null) => any
// type OnError = (error: Error) => any

// function tsOnGet<Model>(
//   ref: Ref<Model>,
//   onResult: OnResult<Model>,
//   onError?: OnError
// ) {
//   console.log("tsOnGet dbFs: %o", dbFs)
//   const d1 = dbFs.collection(ref.collection.path)
//   console.log("tsOnGet d1: %o", d1)
//   const d2 = d1.doc(ref.id)
//   console.log("tsOnGet d2: %o", d2)

//   // return dbFs
//   //   .collection(ref.collection.path)
//   //   .doc(ref.id)
//   return d2
//     .onSnapshot(firestoreSnap => {
//       console.log("tsOnGet onSnapshot: %o", firestoreSnap)
//       const doc = {
//         data: firestoreSnap.data(),
//         ref: tsRef(ref.collection, ref.id)
//       }
//       onResult(doc as Doc<Model>)
//     }, onError)
// }

export type DaoState<K extends keyof CollectionActions> = {
  readonly __dispatch?: Dispatch<any>
  readonly __doc?: Doc<CollectionTypes[K]>
  readonly __validation?: ValidationState
  readonly __unsub?: CallbackFn
  readonly __cb?: CallbackFn
  readonly autosave: boolean
  readonly data: CollectionTypes[K]
  readonly dirty: boolean
  readonly loaded: boolean
  readonly valid: boolean
  readonly node: GetNodefn<CollectionTypes[K]>
  readonly set: CollectionDispatchedActions<K>
  refresh(): void
  save(cb: CallbackFn): void
  delete: (idx: string) => Promise<any>
  readonly ref: {
    collection: Collection<
      K extends keyof KeysOfType<CollectionTypes, CollectionTypes[K]>
        ? CollectionTypes[K]
        : never
    >
    id: string
  }
}

export default function useDao<K extends keyof CollectionActions>(
  collection: K extends keyof CollectionTypes ? K : never,
  id: string = "",
  autosave: boolean = true,
  newDoc: Partial<CollectionTypes[K]> | null = null
) {
  const ref = { collection: db[collection], id: id }
  const ext: DaoState<K> = {
    __dispatch: undefined,
    __doc: undefined,
    __validation: undefined,
    __unsub: undefined,
    __cb: undefined,
    autosave: autosave,
    data: {} as CollectionTypes[K],
    dirty: false,
    loaded: false,
    valid: false,
    node: dispatchWrapperForNode(() =>
      console.log("Node dispatch not set")
    ) as unknown as GetNodefn<CollectionTypes[K]>,
    set: dispatchWrapperForActionsOfCollection(collection, () => {
      console.log("dispatcher never set")
    }),
    refresh: () => console.log("Save has not been setup yet."),
    save: () => console.log("Save has not been setup yet."),
    delete: async (idx: string) => await tsDel(db[collection], idx),
    ref: ref
  }

  const createLock = useRef<boolean>(false)

  const initialState = {
    ...ext
  } as DaoState<K>

  const reducer = reducerWithoutInitialState<DaoState<K>>()
    .case(clearCallback, state => ({ ...state, __cb: undefined }))
    .case(clearDirtyFlag, state => ({ ...state, dirty: false }))
    .case(saveDoc, (state, cb) => ({ ...state, __cb: cb, dirty: true }))
    .caseWithAction(loadDoc, (state, { payload: __unsub, meta }) => ({
      ...state,
      __unsub: __unsub,
      ref: meta?.ref
    }))
    .caseWithAction(resetStateAndLoad, (state, { payload: __unsub, meta }) => ({
      ...state,
      __unsub: __unsub,
      data: {} as CollectionTypes[K],
      ref: meta?.ref,
      loaded: false,
      dirty: false,
      __validation: undefined,
      valid: false,
      __doc: undefined
    }))
    .case(setValidation, (state, __validation) => ({
      ...state,
      __validation: __validation
    }))
    // Received data, which means the validation is complete.
    .caseWithAction(setData, (state, { payload: ref, meta }) => {
      // Validation is asynchronous so we have to ensure a call to us is for the correct
      // collection and id
      if (
        ref.collection.path == state.ref.collection.path &&
        ref.id == state.ref.id
      ) {
        // Ok, got setData for the collection/id we wanted
        return { ...state, loaded: true, ...meta?.state }
      }
      // Nope, late validation, we have already requested a new doc apparently
      console.log("Received late validation data, throwing it away!!!")
      return state
    })
    // Firestore Doc processing.  Determine if we have new doc.
    .case(setDoc, (state, doc) => {
      if (state.dirty && state.loaded) {
        const d: { seconds: number; nanoseconds: number } =
          doc.data.meta.updated
        const s: { seconds: number; nanoseconds: number } =
          state.data.meta.updated

        //unsaved changes, so we need to be careful
        // are the collection or the id different from the current state?
        if (
          state.ref.collection.path == doc.ref.collection.path &&
          state.ref.id == doc.ref.id
        ) {
          // ok, so we have a dirty doc, let's check the timestamp and see which is newer
          // and preserve which is newer

          if (
            d.seconds > s.seconds ||
            (d.seconds == s.seconds && d.nanoseconds > s.nanoseconds)
          ) {
            // the new doc is newer so update and clear dirty flag
            return {
              ...state,
              dirty: false,
              __doc: doc,
              __validation: "PENDING"
            }
          } else {
            // store the new raw doc, that way it is there for comparison on the next save trigger
            return { ...state, __doc: doc }
          }
        } else {
          // This is really strange. We have a brand new doc with diff collection/id
          // the only way it makes sense is on a really slow computer/slow internet.
          // We have to throw it away as I should never get a set for a collection or id
          // I didn't request
          console.log(
            "Received data for collection and id we didn't last request, so throwing away!"
          )
          return state
        }
      }

      // state isn't dirty so we are ok updating
      return {
        ...state,
        __doc: doc,
        ref: { collection: doc.ref.collection, id: doc.ref.id },
        __validation: "PENDING"
      }
    })
    .case(setDispatch, (state, __dispatch) => ({
      ...state,
      __dispatch: __dispatch
    }))
    .case(setNodeProp, (state, node) => ({ ...state, node: node }))
    .case(setSetProp, (state, set) => ({ ...state, set: set }))
    .case(setRefreshState, (state, refresh) => ({ ...state, refresh }))
    .case(setSaveDocFn, (state, saveFn) => ({ ...state, save: saveFn }))
    .default((state, action) => {
      // @ts-ignore
      let data = reducers[collection](state.data, _.cloneDeep(action))

      if (!isEqual(state.data, data)) {
        // Ok, we changed data, so lets set the update field if autosave
        if (state.autosave && data?.meta) {
          data.meta.updated = firebase.firestore.Timestamp.now()
        }
      }

      // yes, we could return the original state if the is equal passed, but
      // I'm paranoid that we will miss something in the lodash compare, so
      // I'm going to shallow merge anyway.
      return { ...state, data: data, dirty: state.autosave } as DaoState<K>
    })

  const [state, dispatch] = useReducer(reducer, initialState)

  const asyncDispatch = useCallback(
    async (action: Action<any>) => {
      // console.log("Action", action)
      // This can eventually turn into a full blown async action handler
      // for now though, just handle the creates
      if (
        action.type.startsWith("__create") ||
        action.type.startsWith("__reset")
      ) {
        const [_a, type, _b] = action.type.split("/") as [
          string,
          keyof typeof typeSchema,
          string
        ]
        if (type) {
          let options = validationOptions as any
          let castType = action?.meta?.castType
          if (castType) {
            options = { ...options, context: { type: castType } }
          }

          const payload = await typeSchema[type].cast(
            action.payload ? action.payload : {},
            options
          )
          const a = { ...action, payload: payload } as Action<any>

          dispatch(a)
        }
      } else {
        dispatch(action)
      }
    },
    [dispatch, state.node, state.set, state.ref]
  )

  // The callback is invoked when receive a new Doc from Firestore
  // We use the validation status to determine if we need to trigger an validation.
  useEffect(() => {
    if (state.__validation == "PENDING") {
      if (state.__doc?.data) {
        dispatch(setValidation("PROCESSING"))

        // select the collection schema validator
        const schema =
          collectionSchema[
            state.ref.collection.path as keyof typeof collectionSchema
          ]

        if (state?.__doc?.ref) {
          const ref = {
            collection: state.__doc.ref.collection,
            id: state.__doc.ref.id
          }

          schema
            .validate(state.__doc.data, validationOptions)
            .then(doc => {
              dispatch(
                setData(ref, {
                  state: {
                    data: state.__doc?.data,
                    dirty: false,
                    valid: true,
                    __validation: "DONE"
                  }
                })
              )
            })
            .catch(e => {
              if (e instanceof yup.ValidationError) {
                // try casting to fix...
                if (state.__doc?.data) {
                  const fixed = schema.cast(state.__doc.data, validationOptions)

                  schema
                    .validate(fixed, validationOptions)
                    .then(doc => {
                      dispatch(
                        setData(ref, {
                          state: {
                            data: fixed,
                            dirty: state.autosave,
                            valid: true,
                            __validation: "DONE"
                          }
                        })
                      )
                    })
                    .catch(e => {
                      // console.log("Fixed doc is invalid")
                      // console.log("Bad doc:: %o", state.__doc?.data)
                      // console.log("Fixed:: %o", fixed)

                      if (Array.isArray(e.errors)) {
                        e.errors.forEach((error: string) =>
                          console.log(`${state.ref.collection.path}: ${error}`)
                        )
                      } else if (e) {
                        console.log(
                          `${state.ref.collection.path}: ${JSON.stringify(
                            e,
                            null,
                            2
                          )}`
                        )
                      }

                      dispatch(
                        setData(ref, {
                          state: {
                            data: fixed,
                            dirty: false,
                            valid: true,
                            __validation: "DONE"
                          }
                        })
                      )
                    })
                }
              }
            })
        }
      }
    }
  }, [state.__validation])

  const docHandler = useCallback(
    (doc: any) => {
      if (doc?.data && doc?.ref) {
        dispatch(setDoc(doc))
      }
    },
    [dispatch]
  )

  const dispatchLoad = useCallback(
    async (reset: boolean = false) => {
      // silly to check again, but better safe than sorry
      if (ref.collection.path && ref.id) {
        const docRef = tsRef(ref.collection, ref.id)
        const meta = { ref: ref }

        // let's see if the doc exists
        const doc = await tsGet(docRef) //ref.collection, ref.id)
        if (doc === null) {
          // ok, doc doesn't exist, so are we being asked to create?
          // also, check lock to ensure we aren't firing off two creates
          if (newDoc !== null && createLock.current === false) {
            // first things, first mark the fact we are creating a document
            createLock.current = true
            // yes we are, so let's cast the partial and add
            // first, get the collection schema
            const schema =
              collectionSchema[
                ref.collection.path as keyof typeof collectionSchema
              ]

            // now let's cast it to fix any missing data
            const casted = schema.cast(
              newDoc,
              validationOptions
            ) as CollectionTypes[K]

            // ok, we have a clean doc, let's save it
            await tsSet<CollectionTypes[K]>(docRef, casted as any)

            // alrighty, done creating let's unlock
            createLock.current = false
          } else if (createLock.current === true) {
            console.log(
              `Info: ${ref.collection.path}/${ref.id} is being created.`
            )

            // return so it doesn't dispatch load
            return
          } else {
            console.log(
              `Error: ${ref.collection.path}/${ref.id} does not exist and newDoc == null.`
            )

            // return so it doesn't dispatch load
            return
          }
        }

        if (reset === true) {
          dispatch(resetStateAndLoad(tsOnGet(docRef, docHandler), meta))
        } else {
          // doc exists so lets load it
          // react memory leak error may appear
          // *** IT'S SAFE TO IGNORE THIS ***
          dispatch(loadDoc(tsOnGet(docRef, docHandler), meta))
        }
      }
    },
    [ref, dispatch, docHandler, newDoc]
  )

  useEffect(() => {
    if (ref.id) {
      if (state.loaded && ref.id != state.ref.id) {
        // dispatch load with reset
        dispatchLoad(true)
      } else if (state.loaded === false && state.__unsub === undefined) {
        // othewise dispatch load

        dispatchLoad(false)
      }

      // I don't think I need this
      // else {
      //   // Man someone is really clicking the screen quick. Or the internet/broswer is jacked
      //   // because the previous load hadn't even finished
      //   dispatchLoad(true)
      // }
    }
  }, [
    JSON.stringify(ref.collection),
    ref.id,
    dispatchLoad,
    state.loaded,
    state.ref.id,
    state.__unsub
  ])

  // WARNING: Vodoo magic here...do not touch
  useEffect(() => {
    if (state.__unsub) {
      // Typeasaurus onGet returns a Firestore unsub
      // The unsub is our protection from memory leaks
      //
      // 1. this effect is only called when __unsub changes.
      // 2. __unsub changes when we a) reset the doc, b) load the doc
      // 3. unsub is an unsubscribe call to the doc snapshot in the collection
      // 4. if unsub changes in the state, then we want react
      //    to call the old unsub for us.  Which it will because we return it as a
      //    cleanup method to this useEffect
      // 6. so, unsub change => useeffect invoked =>  new unsub is returned as cleanup =>
      //    unsub change => cleanup called => return new cleanup as the updated unsub =>
      // 7. on component unmount, react is nice enough to call our cleanup for us
      //    therefore ensuring we have no dangling subscriptions.
      return () => {
        state.__unsub?.()
      }
    }
  }, [state.__unsub])

  useEffect(() => {
    if (state.__dispatch) return
    // with the dispatch in place, then we can init the state setters on
    // the data model and the node
    dispatch(
      setSetProp(
        dispatchWrapperForActionsOfCollection(collection, asyncDispatch)
      )
    )
    dispatch(setRefreshState(() => dispatchLoad(false)))
    dispatch(
      setNodeProp(
        dispatchWrapperForNode(asyncDispatch) as unknown as GetNodefn<
          CollectionTypes[K]
        >
      )
    )
    dispatch(setSaveDocFn((cb: CallbackFn) => dispatch(saveDoc(cb))))
    dispatch(setDispatch(dispatch))
  }, [JSON.stringify(ref.collection), ref.id, state.__dispatch, asyncDispatch])

  useEffect(() => {
    // For saving we ensure the following:
    // 1. the collection is actually loaded
    // 2. the state is dirty - we use this to enable autosave
    // 3. and we have a ref.id to save too
    if (state.loaded == false) return
    if (state.dirty == false) return
    if (state.ref.id == "") return

    // only save if we need too -- check is probably not needed
    if (!isEqual(state.data, state.__doc?.data)) {
      if (state.__cb != undefined) {
        const cb = state.__cb
        // console.log("Saving doc %o", state.data)

        // ok, we have been asked to save and callback
        updateWithMeta(state.ref.collection, state.ref.id, state.data).then(
          () => {
            dispatch(clearCallback())
            cb()
            dispatch(clearDirtyFlag())
          }
        )

        return
      }
      // debounce the save - this is because react reducers are considered pure, which
      // means it can fire off multiple dispatches which may trigger an effect on each
      // dispatch
      const debounce = setTimeout(() => {
        // ensure that every save has a modification timestamp
        // we will also use this to build an undo/redo mech
        // console.log("Saving doc %o", state.data)
        // console.log("Prev doc %o", state.__doc?.data)
        updateWithMeta(state.ref.collection, state.ref.id, state.data).then(
          () => dispatch(clearDirtyFlag())
        )
      }, 200)

      // return an unsub so a subsequent firing of the effect disables
      // a pending save
      return () => clearTimeout(debounce)
    }

    dispatch(clearDirtyFlag())
  }, [state.data, state.dirty, state.__cb, state.ref])

  // return our encapsulated state
  return state
}
